// Content for the /kansas/resources hub. Facts mirror the Kansas pillar guide
// (KansasCeRequirementsPage) and were checked against krec.ks.gov, September 2026.
// Only list guides that are LIVE — never link a card to a page that doesn't exist yet.
// Guide cards are built from KANSAS_GUIDES, so a new guide appears here automatically.

import { KANSAS_GUIDES } from './kansasGuides';

export const KANSAS_HUB = {
  seoTitle: 'Kansas Real Estate CE Guides (2026) | Steady Education',
  seoDescription: 'Plain-English guides to Kansas real estate CE: the 12-hour KREC requirement, Required Core, renewal deadlines, late fees and inactive status.',
  h1: 'Kansas Real Estate Continuing Education Resources',
  intro: "Plain-English guides to Kansas real estate CE — written for salespersons and brokers and checked against Kansas Real Estate Commission (KREC) rules. Find out how many hours you need, what the Kansas Required Core covers, when your license expires, and what happens if you miss the deadline. Every guide cites the KREC source it's based on.",
  reviewedLabel: 'Last reviewed September 2026',
  reviewedIso: '2026-09-27',

  facts: [
    { term: 'Hours per renewal', value: '12 hours every two-year cycle to renew on active status (salespersons and brokers)' },
    { term: 'Mandatory course', value: '3 hours: the approved course titled "Kansas Required Core"' },
    { term: 'Electives', value: '9 hours of any KREC-approved courses — no more than one 3-hour appraisal-type course per renewal' },
    { term: 'Online passing score', value: 'At least 90% on quiz and exam questions for every distance course (K.A.R. 86-1-10)' },
    { term: 'Who reports hours', value: 'Your school reports completions to KREC — you file nothing. We report within 3–5 calendar days; KREC allows up to five.' },
    { term: 'Renewal reminder', value: 'KREC emails a courtesy reminder 45 days before expiration; it recommends finishing CE at least 7 days early' },
    { term: 'Late renewal', value: '$100 late fee; the license is forfeited if not renewed within six months of expiration' },
    { term: 'Code of Ethics', value: 'Not required by KREC — a KREC-approved Code of Ethics course can count as an elective' },
  ],

  guides: [
    {
      url: '/kansas-real-estate-ce-requirements',
      label: 'Pillar Guide',
      title: 'Kansas Real Estate CE Requirements: The Complete 2026 Guide',
      desc: 'Every KREC requirement explained: the 12-hour breakdown, the mandatory Required Core, deadlines, fees and late renewal.',
    },
    ...KANSAS_GUIDES.map(g => ({ url: `/${g.slug}`, label: g.label, title: g.title, desc: g.cardDesc })),
    {
      url: '/kansas/how-to-renew',
      label: 'How to Renew',
      title: 'How to Renew Your Kansas Real Estate License',
      desc: 'The renewal steps end to end — what you complete, what we report to KREC for you, and when to renew.',
    },
  ],

  // Answer text is used verbatim in the FAQPage schema — keep visible text and schema identical.
  faq: [
    {
      q: 'How many CE hours do I need to renew a Kansas real estate license?',
      a: 'Kansas requires 12 hours of KREC-approved continuing education every two-year renewal cycle to renew on active status. Three of those hours must be the Kansas Required Core; the other nine can be any KREC-approved courses, with no more than one three-hour appraisal-type course per renewal.',
      link: { url: '/kansas-real-estate-ce-requirements', label: 'Kansas CE requirements guide' },
    },
    {
      q: 'Do I have to report my CE hours to KREC myself?',
      a: 'No. In Kansas the school reports your course completions to KREC, not you. Schools must file within five calendar days of completion or by your expiration date, whichever comes first. Steady Education reports within 3–5 calendar days, so finish your courses with a few days to spare.',
      link: { url: '/who-reports-ce-to-krec', label: 'Who reports your CE to KREC' },
    },
    {
      q: 'Can I complete all Kansas real estate CE online?',
      a: 'Yes. KREC approves asynchronous distance courses, so all 12 hours, including the Required Core, can be completed online at your own pace. Every distance course requires a score of at least 90% on its quiz and exam questions, which is why Kansas courses end with a substantial final exam.',
      link: { url: '/kansas-real-estate-ce-requirements', label: 'Kansas CE requirements guide' },
    },
    {
      q: 'What happens if I renew on inactive status?',
      a: "You can renew on inactive status without completing CE, but the requirement is deferred, not waived. The missed hours must be made up before KREC will reactivate your license, so renewing inactive only makes sense if you don't plan to practice for a while.",
      link: { url: '/kansas-inactive-license-reactivation', label: 'Reactivating an inactive license' },
    },
    {
      q: 'Does the REALTOR® Code of Ethics count toward Kansas CE?',
      a: 'It can, as an elective. KREC does not require Code of Ethics training, but a KREC-approved Code of Ethics course counts toward your nine elective hours. It can never replace the Kansas Required Core. If you take it for your REALTOR® association, check that they accept the KREC-approved version.',
      link: { url: '/kansas-ce-faq', label: 'Kansas CE FAQ' },
    },
  ],

  sources: [
    { url: 'https://www.krec.ks.gov/licensees/license-renewal/continuing-education', label: 'KREC — Continuing Education', desc: 'Hour requirements, the Required Core, and the approved course and provider directory.' },
    { url: 'https://www.krec.ks.gov/licensees/license-renewal', label: 'KREC — License Renewal', desc: 'Renewal window, fees, late renewal and inactive status.' },
    { url: 'https://licensing.ks.gov/verification/', label: 'Kansas license verification', desc: 'Look up your license status and expiration date.' },
  ],

  ctaSub: 'All 12 KREC-required hours, including the Required Core. Self-paced — we report to KREC for you. School ID ED0190.',
};
